import { reactive, readonly } from 'vue'
import type { FeatureFlag } from './feature-flags'
import featureService from './feature.service'
import cacheService from './feature-flags-cache.service'

type FeatureFlagsMap = Partial<Record<FeatureFlag, boolean>>

interface FeatureFlagsState {
  flags: FeatureFlagsMap
  initialized: boolean
}

const _eagerCache = cacheService.readCache()

let freshFetch: Promise<FeatureFlagsMap> = Promise.resolve({})

const state = reactive<FeatureFlagsState>({
  flags: _eagerCache ?? {},
  initialized: _eagerCache !== null,
})

const setFlags = (flags: FeatureFlagsMap) => {
  state.flags = flags
  state.initialized = true
}

const setFlag = (key: FeatureFlag, value: boolean) => {
  state.flags[key] = value
}

const isEnabled = (key: FeatureFlag): boolean => {
  return state.flags[key] ?? false
}

const isDisabled = (key: FeatureFlag): boolean => {
  return !isEnabled(key)
}

const reset = () => {
  state.flags = {}
  state.initialized = false
}

/**
 * Initializes feature flags during app bootstrap.
 * Uses Stale-While-Revalidate strategy:
 * - If cache exists: load from cache immediately, then revalidate in background
 * - If no cache: fetch from API (blocking)
 * Never throws - errors result in empty flags (fail-closed).
 *
 * @returns Promise that always resolves (never rejects)
 */
const initFeatures = async (): Promise<void> => {
  const cached = cacheService.readCache()
  freshFetch = featureService.fetchFeatureFlags()

  if (cached !== null) {
    setFlags(cached)

    // Revalidate in background (non-blocking)
    freshFetch.then((flags) => {
      if (Object.keys(flags).length > 0) {
        setFlags(flags)
        cacheService.writeCache(flags)
      }
    })
    return
  }

  const flags = await freshFetch
  setFlags(flags)

  if (Object.keys(flags).length > 0) {
    cacheService.writeCache(flags)
  }
}

const freshFlags = () => freshFetch

/** @internal Test-only export for resetting state between tests */
export const _testReset = reset

export default {
  state: readonly(state),
  setFlags,
  setFlag,
  isEnabled,
  isDisabled,
  initFeatures,
  freshFlags,
}
